"use client";

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

// Import translation files
import en from '../locales/en.json';
import es from '../locales/es.json';
import ja from '../locales/ja.json';
import ko from '../locales/ko.json';
import zh from '../locales/zh.json';

// ============================================
// Supported Languages
// ============================================
export type SupportedLanguage = 'en' | 'es' | 'ja' | 'ko' | 'zh';

export const SUPPORTED_LANGUAGES: SupportedLanguage[] = ['en', 'es', 'ja', 'ko', 'zh'];

// ============================================
// Translation Types
// ============================================
export interface Translations {
    // Left Panel
    chats: string;
    searchChats: string;
    sortBy: string;
    newest: string;
    aToZ: string;
    zToA: string;
    conversations: string;
    messages: string;
    noResultsFound: string;
    noConversationsFound: string;
    searching: string;
    matchedMessages: string;
    isTyping: string;

    // Center Panel
    typeMessage: string;
    send: string;
    reply: string;
    copy: string;
    delete: string;
    download: string;
    today: string;
    yesterday: string;
    selectConversation: string;
    noMessages: string;
    sending: string;
    sent: string;
    delivered: string;
    read: string;
    readOn: string;
    failed: string;
    retry: string;
    loading: string;
    loadingMore: string;

    // Right Panel
    members: string;
    sharedMedia: string;
    sharedFiles: string;
    photos: string;
    videos: string;
    files: string;
    images: string;
    noSharedMedia: string;
    noSharedFiles: string;
    noVideosShared: string;
    noImagesShared: string;
    noDocumentsShared: string;

    // Chat Widget
    chat: string;
    chatDetails: string;
    minimize: string;
    maximize: string;
    close: string;

    // File Upload
    dragDropFiles: string;
    orClickToSelect: string;
    uploadingFile: string;
    fileTooBig: string;
    maxFileSize: string;
    fileSizeExceedsLimit: string;
    filesSelected: string;
    clearAll: string;
    sendingAlbum: string;

    // Notifications
    newMessage: string;
    newMessages: string;
    notificationSettings: string;
    enableNotifications: string;
    soundNotifications: string;
    customMessage: string;
    reactionNotificationTitle: string;
    reactionNotificationDesc: string;
    readReceiptNotificationTitle: string;
    readReceiptNotificationDesc: string;

    // Time
    justNow: string;
    minutesAgo: string;
    hoursAgo: string;
    daysAgo: string;
    weeksAgo: string;
    monthsAgo: string;
    yearsAgo: string;

    // Status
    online: string;
    offline: string;
    open: string;
    closed: string;

    // Actions
    cancel: string;
    confirm: string;
    save: string;
    edit: string;

    // Emoji & GIF & Stickers
    emoji: string;
    gif: string;
    stickers: string;
    searchGifs: string;
    searchStickers: string;
    trending: string;
    sendSticker: string;
    sendStickers: string;
    poweredByKlipy: string;

    // Voice Message
    recording: string;
    tapToRecord: string;
    holdToRecord: string;
    releaseToSend: string;
    voiceMessageReady: string;
    uploadingVoice: string;

    // Media Types
    image: string;
    video: string;
    file: string;
    voice: string;
    location: string;
    locationReadyToSend: string;
    sendingLocation: string;

    // Search
    searchMessages: string;
    noSearchResults: string;

    // Contact Info
    email: string;
    phone: string;
    notProvided: string;

    // Messages
    thisMessageWasDeleted: string;
    deleteForMe: string;
    unsend: string;
    openInMaps: string;

    // Errors
    errorSendingMessage: string;
    errorLoadingMessages: string;
    messageSendFailed: string;
    photoUploadFailed: string;
    attachmentUploadFailed: string;
    connectionError: string;
    checkInternetConnection: string;
    reconnecting: string;

    // Members
    viewMembers: string;
    selectReaction: string;

    // Connection Status
    connecting: string;
    connected: string;
    disconnected: string;
    syncing: string;
    synced: string;
    connectionFailed: string;
    kickedOffline: string;
    sessionExpired: string;
    unknown: string;
    connectingToChat: string;
    groupId: string;
    openRoom: string;
    closeRoom: string;
    information: string;
    status: string;

    // Reply Message Previews
    replySticker: string;
    replyPhoto: string;
    replyVideo: string;
    replyVoiceMessage: string;
    replyLocation: string;
    replyMessage: string;
    uploading: string;
}

// ============================================
// Translation Data (imported from JSON files)
// ============================================
const translations: Record<SupportedLanguage, Translations> = {
    en: en as Translations,
    es: es as Translations,
    ja: ja as Translations,
    ko: ko as Translations,
    zh: zh as Translations,
};

// ============================================
// Language Context
// ============================================
interface LanguageContextType {
    language: SupportedLanguage;
    setLanguage: (lang: SupportedLanguage) => void;
    t: Translations;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// ============================================
// Language Provider
// ============================================
interface LanguageProviderProps {
    children: React.ReactNode;
    initialLanguage?: SupportedLanguage;
}

export function LanguageProvider({ children, initialLanguage }: LanguageProviderProps) {
    const [language, setLanguageState] = useState<SupportedLanguage>(() => {
        // Try to get language from localStorage first
        if (typeof window !== 'undefined') {
            const storedLang = localStorage.getItem('lang');
            console.log('[LanguageContext] Init - localStorage lang:', storedLang, 'initialLanguage prop:', initialLanguage);
            if (storedLang && SUPPORTED_LANGUAGES.includes(storedLang.toLowerCase().substring(0, 2) as SupportedLanguage)) {
                const resolvedLang = storedLang.toLowerCase().substring(0, 2) as SupportedLanguage;
                console.log('[LanguageContext] Using language from localStorage:', resolvedLang);
                return resolvedLang;
            }
        }
        console.log('[LanguageContext] Using default/initial language:', initialLanguage || 'en');
        return initialLanguage || 'en';
    });

    const setLanguage = useCallback((lang: SupportedLanguage) => {
        if (SUPPORTED_LANGUAGES.includes(lang)) {
            setLanguageState(lang);
            console.log('[LanguageContext] Language changed to:', lang);
        }
    }, []);

    // Listen for language change events from host app
    useEffect(() => {
        const handleLanguageChange = (event: CustomEvent<{ language: string }>) => {
            const newLang = event.detail.language?.toLowerCase().substring(0, 2) as SupportedLanguage;
            if (SUPPORTED_LANGUAGES.includes(newLang)) {
                setLanguage(newLang);
            }
        };

        window.addEventListener('jfc-chat:change-language', handleLanguageChange as EventListener);

        return () => {
            window.removeEventListener('jfc-chat:change-language', handleLanguageChange as EventListener);
        };
    }, [setLanguage]);

    // Also listen for localStorage changes (when jfc-app changes language)
    useEffect(() => {
        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === 'lang' && event.newValue) {
                const newLang = event.newValue.toLowerCase().substring(0, 2) as SupportedLanguage;
                if (SUPPORTED_LANGUAGES.includes(newLang)) {
                    setLanguage(newLang);
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [setLanguage]);

    const t = translations[language];

    return (
        <LanguageContext.Provider value={{ language, setLanguage, t }}>
            {children}
        </LanguageContext.Provider>
    );
}

// ============================================
// Hook
// ============================================
export function useLanguage() {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
}

// Export translations for external use
export { translations };
